/**
 * Deploy-time configuration
 *
 * Production builds load `/runtime-config.js` (served by Laravel) before the
 * application bundle. It sets `window.__APP_RUNTIME_CONFIG__` to the values the
 * deployment knows but the image build did not, such as generated keys. The
 * dev server never loads it, so development keeps its build-time values.
 */

/** Keys Laravel may serve; each mirrors the build-time `import.meta.env` name. */
export type RuntimeConfigKey =
  | 'API_BASE_URL'
  | 'CLIENT_SERVER_KEY'
  | 'GOOGLE_MAPS_API_KEY'
  | 'REVERB_APP_KEY'
  | 'VITE_REVERB_HOST'
  | 'VITE_REVERB_PORT'
  | 'VITE_REVERB_SCHEME'
  | 'VITE_VAPID_PUBLIC_KEY'

declare global {
  interface Window {
    __APP_RUNTIME_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>
  }
}

/**
 * Returns the deploy-time value for `key`, falling back to the build-time value.
 *
 * Pass the build-time value as a literal `import.meta.env.KEY` access: Vite
 * replaces only static accesses, so this helper cannot look it up itself.
 *
 * @param key - Runtime configuration key
 * @param buildValue - The matching `import.meta.env` value
 * @returns The non-empty runtime value, otherwise `buildValue`
 * @example
 * ```typescript
 * const key = runtimeEnv('REVERB_APP_KEY', import.meta.env.REVERB_APP_KEY)
 * ```
 */
export function runtimeEnv(key: RuntimeConfigKey, buildValue: string | undefined): string | undefined {
  const runtimeValue = typeof window === 'undefined' ? undefined : window.__APP_RUNTIME_CONFIG__?.[key]

  return runtimeValue ? runtimeValue : buildValue
}
